import * as Sentry from "@sentry/browser";
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Navigate, Route, Routes, useParams } from "react-router";
import { useManagerVariant } from "../../hooks/useManagerVariant";
import {
  DEFAULT_SUBPAGE as DEFAULT_PARAGRAPH6_SUBPAGE,
  Paragraph6View
} from "../../modules/Paragraph6/Paragraph6View";
import { ROUTES } from "../../routes";
import { isValidUuid } from "../../utils/isValidUuid";
import {
  DEFAULT_SUBPAGE as DEFAULT_DELIVERY_OVERVIEW_SUBPAGE,
  DeliveryOverview
} from "../DeliveryOverview/DeliveryOverview";
import { DirektvermarktungView } from "../Direktvermarktung/DirektvermarktungView";
import {
  DEFAULT_ENERGY_DATA_SUBPAGE,
  EnergyDataView
} from "../EnergyData/EnergyDataView/EnergyDataView";
import { GuaranteeOfOriginPage } from "../GuaranteeOfOrigin/GuaranteeOfOriginPage";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useMenuPermissions } from "../Menu/useMenuPermissions";
import {
  DEFAULT_SUBPAGE as DEFAULT_MSM_PROJECT_VIEW_SUBPAGE,
  MsmProjectView
} from "../MsmProjectView/MsmProjectView";
import { RevenueMonitoringView } from "../RevenueMonitoring/RevenueMonitoringView";
import { Todos } from "../Todos/Todos";
import { Mode } from "../Todos/Todos.types";
import { TodosV2 } from "../TodosV2/TodosV2";

function ManagerVariantRoutes() {
  const { projectId } = useParams();
  const isInvalidProjectId = !!projectId && !isValidUuid(projectId);
  const { variantId, isLoading, error } = useManagerVariant(
    isInvalidProjectId ? undefined : projectId
  );
  const { t } = useTranslation();

  const redirectPath = ROUTES.dashboard;

  const customErrors = {
    404: t("errors.ProjectNotFound")
  };

  useEffect(() => {
    if (isInvalidProjectId) {
      Sentry.captureMessage("Invalid projectId in manager route", {
        level: "warning",
        extra: { projectId }
      });
    }
  }, [isInvalidProjectId, projectId]);

  const ElementDeliveryOverview = (
    <DeliveryOverview
      projectId={projectId}
      variantId={variantId ?? undefined}
    />
  );
  const ElementGuaranteeOfOriginPage = (
    <>{variantId && <GuaranteeOfOriginPage variantId={variantId} />}</>
  );
  const ElementParagraph6View = (
    <>
      {variantId && projectId && (
        <RequireParagraph6 variantId={variantId}>
          <Paragraph6View projectId={projectId} variantId={variantId} />
        </RequireParagraph6>
      )}
    </>
  );
  const ElementTodosV2 = <>{variantId && <TodosV2 variantId={variantId} />}</>;
  const ElementTodos = (
    <>{variantId && <Todos mode={Mode.Onboarding} variantId={variantId} />}</>
  );

  if (!projectId || isInvalidProjectId) {
    return <Navigate replace to={redirectPath} />;
  }

  return (
    <LoadOrError
      customErrors={customErrors}
      error={error}
      errorRedirectTo={redirectPath}
      loading={isLoading}
    >
      <Routes>
        <Route
          element={<Navigate replace to={DEFAULT_MSM_PROJECT_VIEW_SUBPAGE} />}
          index
        />
        <Route
          element={<MsmProjectView projectId={projectId} />}
          path=":projectPage"
        >
          <Route element={<MsmProjectView projectId={projectId} />} index />
          <Route element={<MsmProjectView projectId={projectId} />} path="*" />
        </Route>
        <Route path="lieferungen-uebersicht">
          <Route
            element={
              <Navigate replace to={DEFAULT_DELIVERY_OVERVIEW_SUBPAGE} />
            }
            index
          />
          <Route element={ElementDeliveryOverview} path=":tab">
            <Route element={ElementDeliveryOverview} index />
            <Route element={ElementDeliveryOverview} path="*" />
          </Route>
        </Route>
        <Route element={ElementGuaranteeOfOriginPage} path="herkunftsnachweis">
          <Route element={ElementGuaranteeOfOriginPage} index />
          <Route element={ElementGuaranteeOfOriginPage} path="*" />
        </Route>
        <Route path="paragraph6">
          <Route
            element={<Navigate replace to={DEFAULT_PARAGRAPH6_SUBPAGE} />}
            index
          />
          <Route element={ElementParagraph6View} path=":tab">
            <Route element={ElementParagraph6View} index />
            <Route element={ElementParagraph6View} path="*" />
          </Route>
        </Route>
        <Route element={<DirektvermarktungView />} path="vermarktung">
          <Route element={<DirektvermarktungView />} index />
          <Route element={<DirektvermarktungView />} path="*" />
        </Route>
        <Route element={<RevenueMonitoringView />} path="erloesmonitoring">
          <Route element={<RevenueMonitoringView />} index />
          <Route element={<RevenueMonitoringView />} path="*" />
        </Route>
        <Route path="energiedaten">
          <Route
            element={<Navigate replace to={DEFAULT_ENERGY_DATA_SUBPAGE} />}
            index
          />
          <Route path=":tab">
            <Route
              element={
                <>{variantId && <EnergyDataView variantId={variantId} />}</>
              }
              index
            />
            <Route
              element={
                <>{variantId && <EnergyDataView variantId={variantId} />}</>
              }
              path="*"
            />
          </Route>
        </Route>
        <Route element={ElementTodosV2} path="aufgaben">
          <Route element={ElementTodosV2} index />
          <Route element={ElementTodosV2} path="*" />
        </Route>
        <Route element={ElementTodos} path="onboarding">
          <Route element={ElementTodos} index />
          <Route element={ElementTodos} path="*" />
        </Route>
      </Routes>
    </LoadOrError>
  );
}

interface RequireParagraph6Props {
  variantId: number;
  children: ReactNode;
}

function RequireParagraph6({ variantId, children }: RequireParagraph6Props) {
  const { projectId } = useParams();
  const { paragraph6Enabled, isLoadingParagraph6 } = useMenuPermissions({
    variantId
  });

  if (!isLoadingParagraph6 && !paragraph6Enabled) {
    return (
      <Navigate
        replace
        to={
          projectId
            ? generatePath(ROUTES.project.entry, { projectId })
            : ROUTES.startPage
        }
      />
    );
  }

  return children;
}

export { ManagerVariantRoutes };
